<template>
    <Dialog :visible="visible" @update:visible="$emit('update:visible', $event)" :modal="true"
            header="Zonas del salón" :style="{width: '480px'}" :breakpoints="{'640px': '94vw'}">
        <p class="zm-hint">Cada zona es un plano propio en el mapa (Salón, Terraza, Barra, Segundo piso…).
            El orden es el de las pestañas.</p>

        <div class="zm-list">
            <div v-for="(z, index) in zones" :key="z.id" class="zm-row">
                <div class="zm-order">
                    <Button icon="pi pi-chevron-up" class="p-button-text p-button-sm p-button-rounded"
                            :disabled="index === 0 || busy" aria-label="Subir" @click="move(z, -1)" />
                    <Button icon="pi pi-chevron-down" class="p-button-text p-button-sm p-button-rounded"
                            :disabled="index === zones.length - 1 || busy" aria-label="Bajar" @click="move(z, 1)" />
                </div>
                <InputText v-model="names[z.id]" class="zm-name" maxlength="40"
                           :aria-label="'Nombre de la zona ' + z.name"
                           @keydown.enter="rename(z)" @blur="rename(z)" />
                <span class="zm-count">{{ z.tableCount }} {{ z.tableCount === 1 ? 'mesa' : 'mesas' }}</span>
                <Button icon="pi pi-trash" class="p-button-text p-button-danger p-button-sm p-button-rounded"
                        :disabled="busy" :aria-label="'Eliminar ' + z.name" @click="confirmDelete(z)" />
            </div>
            <div v-if="!zones.length" class="zm-empty">Aún no hay zonas. Crea la primera abajo.</div>
        </div>

        <form class="zm-new" @submit.prevent="create">
            <InputText v-model="newName" placeholder="Nueva zona (ej. Terraza)" maxlength="40" class="zm-name" />
            <Button type="submit" label="Agregar" icon="pi pi-plus" :disabled="!newName.trim() || busy" :loading="creating" />
        </form>

        <template #footer>
            <Button label="Listo" @click="$emit('update:visible', false)" />
        </template>
    </Dialog>
</template>

<script>
import httpRequest from '../../service/httpRequest';
import { reorderZones } from '../../service/floorLayout';

export default {
    name: 'ZoneManagerDialog',
    props: {
        visible: { type: Boolean, default: false },
        zones: { type: Array, default: () => [] },   // [{id, name, order, tableCount}] en orden
    },
    emits: ['update:visible', 'changed', 'created'],
    data() {
        return { names: {}, newName: '', busy: false, creating: false };
    },
    watch: {
        zones: { immediate: true, handler(zones) { this.syncNames(zones); } },
        visible(open) { if (open) { this.newName = ''; this.syncNames(this.zones); } },
    },
    methods: {
        syncNames(zones) {
            const names = {};
            for (const z of zones) names[z.id] = z.name;
            this.names = names;
        },
        fail(res, summary) {
            this.$toast.add({ severity: 'warn', summary, detail: (res && res.error) || 'Intenta de nuevo.', life: 4000 });
        },
        async create() {
            const name = this.newName.trim();
            if (!name) return;
            this.creating = true;
            const res = await httpRequest.saveRestaurantZone({ name });
            this.creating = false;
            if (res && res.status === 200) {
                this.newName = '';
                this.$emit('created', res.data);
                this.$emit('changed');
            } else {
                this.fail(res, 'No se pudo crear la zona');
            }
        },
        async rename(zone) {
            const name = (this.names[zone.id] || '').trim();
            if (name === zone.name) return;
            if (!name) { this.names[zone.id] = zone.name; return; }
            this.busy = true;
            const res = await httpRequest.saveRestaurantZone({ id: zone.id, name });
            this.busy = false;
            if (res && res.status === 200) {
                this.$emit('changed');
            } else {
                this.names[zone.id] = zone.name;
                this.fail(res, 'No se pudo renombrar');
            }
        },
        async move(zone, direction) {
            const changes = reorderZones(this.zones, zone.id, direction);
            if (!changes.length) return;
            this.busy = true;
            const results = await Promise.all(changes.map(c => httpRequest.saveRestaurantZone(c)));
            this.busy = false;
            const failed = results.find(r => !r || r.status !== 200);
            if (failed) this.fail(failed, 'No se pudo reordenar');
            this.$emit('changed');
        },
        confirmDelete(zone) {
            if (zone.tableCount) {
                this.$toast.add({ severity: 'info', summary: `«${zone.name}» tiene mesas`,
                                  detail: 'Muévelas a otra zona o elimínalas antes de borrar la zona.', life: 4500 });
                return;
            }
            this.$confirm.require({
                message: `¿Eliminar la zona «${zone.name}»?`, header: 'Eliminar zona',
                icon: 'pi pi-exclamation-triangle', acceptLabel: 'Eliminar', rejectLabel: 'Cancelar',
                acceptClass: 'p-button-danger',
                accept: async () => {
                    this.busy = true;
                    const res = await httpRequest.deleteRestaurantZone(zone.id);
                    this.busy = false;
                    if (res && res.status === 200) this.$emit('changed');
                    else this.fail(res, 'No se pudo eliminar');
                },
            });
        },
    },
};
</script>

<style scoped>
.zm-hint { color: var(--text-color-secondary); font-size: .88rem; margin-top: 0; }
.zm-list { display: flex; flex-direction: column; gap: .4rem; margin-bottom: 1rem; }
.zm-row { display: flex; align-items: center; gap: .5rem; }
.zm-order { display: flex; flex-direction: column; }
.zm-order :deep(.p-button) { height: 1.4rem; width: 1.8rem; }
.zm-name { flex: 1; min-width: 0; }
.zm-count { color: var(--text-color-secondary); font-size: .82rem; white-space: nowrap; min-width: 4.2rem; text-align: right; }
.zm-empty { color: var(--text-color-secondary); padding: .75rem 0; text-align: center; }
.zm-new { display: flex; gap: .5rem; border-top: 1px solid var(--surface-border); padding-top: 1rem; }
</style>
